import { useIsCopilotEnabled } from '@common/Copilot/useIsCopilotEnabled';
import { useProjectState } from '@common/git/Project/hooks';
import { useOnboardingDetail } from '@common/onboarding/hooks';
import { QueryKeys } from '@common/queries/common';
import { Entity } from '@common/types/Entity';
import { getIDEUrl, getSqlIDEUrl, Private_Routes } from '@common/url';
import { HUB_URL } from '@pages/PackageHub/constants';

import { useGlobalVariables } from '../../context/globalConfig/securedConfigHook';
import { useUser } from '../../context/user';
import { useGraphQlQuery } from '../../data/util';
import { UserIndustry } from './constants';
import { GetUserOnboardingDetails } from './query';

/**
 * @returns the pending state and the URL to redirect to based on the ser's onboarding details and the global configuration.
 */
export const useOnboardingUrl = () => {
  const { user } = useUser();

  const globalConf = useGlobalVariables();
  const { pipeline: sparkPipeline, sqlProject, analystProject } = useOnboardingDetail();
  const variables = { uid: user?.id };

  const { data: userOnboardingDetails, isLoading: isUserOnboardingDetailsLoading } = useGraphQlQuery(
    [QueryKeys.UserOnboardingDetails, 'UserDetail', variables],
    GetUserOnboardingDetails,
    undefined,
    variables
  );
  const isOnboardingV2Enabled = globalConf.isOnboardingV2Enabled;
  const isProfessionalEditionEnabled = globalConf.isProfessionalEditionEnabled;
  const isCopilotEnabled = useIsCopilotEnabled();
  const canDoSql = isCopilotEnabled && sqlProject;

  const sparkOnboardingUrl = sparkPipeline
    ? getIDEUrl({ entity: Entity.Pipeline, uid: sparkPipeline?._id })
    : undefined;
  const onboardingHomeUrl = Private_Routes.Onboarding.getUrl();

  if (isUserOnboardingDetailsLoading) {
    return { pending: true };
  }

  if (isProfessionalEditionEnabled) {
    return { pending: false, url: Private_Routes.Home.getUrl() };
  }

  if (isOnboardingV2Enabled) {
    const { profession, industry } = userOnboardingDetails?.User || {};

    // If profession or industry is not set, it means the user has not completed the onboarding questionnaire
    if (!profession || !industry) {
      return { pending: false, url: null };
    }

    // Redirect to template hub if the industry is OTHER since backend considers each category as a different industry and creates a new project for each one which is not intended for "Other" category.
    // We don't have any templates for HOSPITALITY hence redirecting to template hub for that as well.
    if (industry === UserIndustry.OTHER || industry === UserIndustry.HOSPITALITY) {
      return { pending: false, url: HUB_URL.TEMPLATE };
    }

    if (analystProject) {
      return { pending: false, url: getSqlIDEUrl({ uid: analystProject._id }) };
    }

    return { pending: false, url: sparkOnboardingUrl };
  }

  if (canDoSql) {
    return { pending: false, url: onboardingHomeUrl };
  }

  return { pending: false, url: sparkOnboardingUrl };
};

export const useIsOnboardingProject = () => {
  const { industry } = useProjectState();
  if (!industry) {
    return false;
  }
  if (industry === UserIndustry.OTHER || industry === UserIndustry.HOSPITALITY) {
    return false;
  }
  return true;
};
